import { useDroppable } from '@dnd-kit/core'
import { AddIcon, Text } from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useFormBuilderStore } from '../store'

interface DropZoneProps {
	id: string
	containerId?: string
	index: number
	className?: string
}

interface ColumnDropZoneProps {
	id: string
	containerId: string
	index: number
	colIdx: number
}

export function DropZone({ id, containerId, index, className = '' }: DropZoneProps) {
	const { isOver, setNodeRef, active } = useDroppable({
		id,
		data: {
			type: 'canvas-dropzone',
			containerId,
			index,
			isRootDropZone: !containerId,
		},
	})
	const activeComponent = active?.data.current?.component
	const { canPlaceChild } = useFormBuilderStore()
	const [isNear, setIsNear] = useState(false)
	const dropzoneRef = useRef<HTMLDivElement>(null)
	const canPlace = canPlaceChild(activeComponent, containerId)

	useEffect(() => {
		if (!active) {
			setIsNear(false)
		}
	}, [active])

	useEffect(() => {
		const handleMouseMove = (event: MouseEvent) => {
			if (!dropzoneRef.current || !active) return

			const rect = dropzoneRef.current.getBoundingClientRect()
			const mouseY = event.clientY

			const centerY = rect.top + rect.height / 2
			const distance = Math.abs(mouseY - centerY)

			setIsNear(isNear ? distance <= 50 : distance <= 40)
		}

		document.addEventListener('mousemove', handleMouseMove)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
		}
	}, [active, isNear])

	const combinedRef = (element: HTMLDivElement) => {
		dropzoneRef.current = element
		setNodeRef(element)
	}

	return (
		<div
			ref={combinedRef}
			data-testid={`dropzone-${id}`}
			data-dropzone-container={containerId || 'root'}
			data-dropzone-index={index}
			className={`w-full min-h-0 transition-all duration-200 flex items-center justify-center ${
				(isNear && active) || isOver
					? `h-15 border-2 border-dashed rounded-md ${
							!canPlace && isOver
								? 'bg-error-300 border-error-300'
								: !canPlace
									? 'bg-error-50 border-error-300'
									: isOver
										? 'bg-primary-300 border-primary-300'
										: 'bg-primary-50 border-primary-300'
						}`
					: 'h-0 border-0 border-transparent'
			} ${className}`}>
			{((isNear && active) || isOver) && <AddIcon className={`${!canPlace ? 'fill-error-600' : 'fill-primary-600'}`} />}
		</div>
	)
}

export function ColumnDropZone({ id, containerId, index, colIdx }: ColumnDropZoneProps) {
	const { isOver, setNodeRef, active } = useDroppable({
		id,
		data: {
			type: 'canvas-dropzone',
			containerId,
			index,
			isRootDropZone: false,
		},
	})
	const activeComponent = active?.data.current?.component
	const { canPlaceChild } = useFormBuilderStore()
	const canPlace = canPlaceChild(activeComponent, containerId)

	return (
		<div
			ref={setNodeRef}
			data-testid={`dropzone-${id}`}
			data-dropzone-container={containerId}
			data-dropzone-index={index}
			className={`h-full min-h-[60px] rounded-md flex items-center justify-center transition-colors duration-200 ${
				!canPlace && isOver
					? 'bg-error-50 border-2 border-dashed border-error-300'
					: isOver
						? 'bg-primary-50 border-2 border-dashed border-primary-300'
						: 'bg-neutral-50 border border-dashed border-neutral-300'
			}`}>
			{!canPlace && isOver ? (
				<AddIcon className='fill-error-600' />
			) : isOver ? (
				<AddIcon className='fill-primary-600' />
			) : (
				<Text variant='xsmall' className='text-neutral-400'>
					Column {colIdx + 1}
				</Text>
			)}
		</div>
	)
}
